import { useCallback, useEffect, useState } from "react";
import type { EqmsController } from "../../../controllers/useEqms";
import type { CreateDocTagRequest, CreateDocTypeRequest, DocTag, DocType } from "../../../models/eqms";
import { createDocTag, createDocType, deleteDocTag, deleteDocType, listDocTags, listDocTypes } from "../../../controllers/eqmsApi";
import { friendlyMessage } from "../../../controllers/projectApi";

// 관리: 워크스페이스 마스터 데이터 — 문서유형(자동채번 규칙 포함)과 태그.
export function EqmsAdminView({ eqms }: { eqms: EqmsController }) {
  const workspaceId = eqms.workspaceId;
  const [docTypes, setDocTypes] = useState<DocType[]>([]);
  const [docTags, setDocTags] = useState<DocTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const [types, tags] = await Promise.all([
        listDocTypes(eqms.getToken, workspaceId),
        listDocTags(eqms.getToken, workspaceId),
      ]);
      setDocTypes(types);
      setDocTags(tags);
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [eqms.getToken, workspaceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function addDocType(req: CreateDocTypeRequest) {
    if (!workspaceId) return;
    setError(null);
    try {
      await createDocType(eqms.getToken, workspaceId, req);
      await reload();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function removeDocType(id: string) {
    setError(null);
    try {
      await deleteDocType(eqms.getToken, id);
      setDocTypes((cur) => cur.filter((t) => t.id !== id));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function addDocTag(req: CreateDocTagRequest) {
    if (!workspaceId) return;
    setError(null);
    try {
      await createDocTag(eqms.getToken, workspaceId, req);
      await reload();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function removeDocTag(id: string) {
    setError(null);
    try {
      await deleteDocTag(eqms.getToken, id);
      setDocTags((cur) => cur.filter((t) => t.id !== id));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  if (!workspaceId) {
    return <div className="note">워크스페이스를 먼저 선택하세요.</div>;
  }

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>관리 · 마스터 데이터</h3>

      {error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      )}

      {loading ? (
        <div className="center">불러오는 중…</div>
      ) : (
        <>
          <DocTypeSection docTypes={docTypes} onCreate={addDocType} onDelete={removeDocType} />
          <DocTagSection docTags={docTags} onCreate={addDocTag} onDelete={removeDocTag} />
        </>
      )}
    </div>
  );
}

function docNumberExample(prefix: string, digits: number): string {
  return `${prefix}-${"1".padStart(digits, "0")}`;
}

function DocTypeSection({
  docTypes,
  onCreate,
  onDelete,
}: {
  docTypes: DocType[];
  onCreate: (req: CreateDocTypeRequest) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [prefix, setPrefix] = useState("");
  const [digits, setDigits] = useState(3);
  const [sortOrder, setSortOrder] = useState(0);
  const [busy, setBusy] = useState(false);

  const example = prefix.trim() ? docNumberExample(prefix.trim(), digits) : "";

  async function submit() {
    if (!name.trim() || !prefix.trim()) return;
    setBusy(true);
    try {
      await onCreate({ name, prefix, digits, sortOrder });
      setName("");
      setPrefix("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="info-card" style={{ marginBottom: 20 }}>
      <h4>문서유형</h4>
      <table className="sdui-table">
        <thead>
          <tr>
            <th>이름</th>
            <th>접두사</th>
            <th>자릿수</th>
            <th>정렬</th>
            <th>예시 번호</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {docTypes.map((t) => (
            <tr key={t.id}>
              <td>{t.name}</td>
              <td className="mono">{t.prefix}</td>
              <td>{t.digits}</td>
              <td>{t.sortOrder}</td>
              <td className="mono">{docNumberExample(t.prefix, t.digits)}</td>
              <td>
                <button className="btn" style={{ width: "auto" }} onClick={() => void onDelete(t.id)}>
                  삭제
                </button>
              </td>
            </tr>
          ))}
        </tbody>
        {docTypes.length === 0 && (
          <tfoot>
            <tr>
              <td colSpan={6} className="sdui-table-empty">
                문서유형이 없습니다.
              </td>
            </tr>
          </tfoot>
        )}
      </table>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginTop: 12 }}>
        <label className="sdui-field" style={{ minWidth: 160 }}>
          <span className="sdui-label">이름</span>
          <input className="sdui-input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="sdui-field" style={{ minWidth: 100 }}>
          <span className="sdui-label">접두사</span>
          <input className="sdui-input" value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase())} />
        </label>
        <label className="sdui-field" style={{ minWidth: 90 }}>
          <span className="sdui-label">자릿수</span>
          <input
            type="number"
            min={1}
            max={8}
            className="sdui-input"
            value={digits}
            onChange={(e) => setDigits(Math.max(1, Number(e.target.value) || 1))}
          />
        </label>
        <label className="sdui-field" style={{ minWidth: 90 }}>
          <span className="sdui-label">정렬</span>
          <input
            type="number"
            className="sdui-input"
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
          />
        </label>
        <div className="sdui-field" style={{ minWidth: 120 }}>
          <span className="sdui-label">예시</span>
          <span className="tag mono">{example || "-"}</span>
        </div>
        <button className="btn primary" style={{ width: "auto" }} disabled={!name.trim() || !prefix.trim() || busy} onClick={submit}>
          {busy ? "생성 중…" : "＋ 추가"}
        </button>
      </div>
    </div>
  );
}

function DocTagSection({
  docTags,
  onCreate,
  onDelete,
}: {
  docTags: DocTag[];
  onCreate: (req: CreateDocTagRequest) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#2f6fed");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await onCreate({ name, color });
      setName("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="info-card">
      <h4>태그</h4>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {docTags.map((t) => (
          <span
            key={t.id}
            className="tag"
            style={{ background: t.color || undefined, display: "inline-flex", gap: 6, alignItems: "center" }}
          >
            {t.name}
            <button className="btn" style={{ width: "auto", padding: "0 4px" }} onClick={() => void onDelete(t.id)}>
              ✕
            </button>
          </span>
        ))}
        {docTags.length === 0 && <div className="sdui-list-empty">태그가 없습니다.</div>}
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label className="sdui-field" style={{ minWidth: 160 }}>
          <span className="sdui-label">이름</span>
          <input className="sdui-input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="sdui-field" style={{ minWidth: 100 }}>
          <span className="sdui-label">색상</span>
          <input type="color" className="sdui-input" value={color} onChange={(e) => setColor(e.target.value)} />
        </label>
        <button className="btn primary" style={{ width: "auto" }} disabled={!name.trim() || busy} onClick={submit}>
          {busy ? "생성 중…" : "＋ 추가"}
        </button>
      </div>
    </div>
  );
}
